import {clamp} from '@benchling/js-utils';

import {Table} from 'notebook/tables/constants/TableConstants';
import {
  MultiAxisDimSelection,
  MultiSelectionBox,
  SelectionRange,
  TableIndex,
} from 'notebook/tables/constants/TableSelectionConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';

/**
 * Computes the normalized selection box with min/max bounds.
 * This is a simple box based on start/end indices - does NOT expand for merged regions.
 * Returns null if either start or end index is null.
 */
export function computeSelectionBox(
  multiStartIndex: TableIndex | null,
  multiEndIndex: TableIndex | null
): MultiSelectionBox | null {
  if (multiStartIndex == null || multiEndIndex == null) {
    return null;
  }
  return TableUtil.indicesToCorners(multiStartIndex, multiEndIndex);
}

/**
 * Computes whether the given index is within the table bounds.
 */
export function computeIsRealIndex(index: TableIndex | null, table: Table): boolean {
  if (index == null) {
    return false;
  }
  const firstIndex: TableIndex = [0, 0];
  const lastIndex: TableIndex = [table.table.length - 1, table.table[0].length - 1];
  return TableUtil.isIndexBetween(firstIndex, lastIndex, index);
}

/**
 * Returns whether the cell at [row, column] falls within the given selection box.
 */
export function computeIsCellSelected(row: number, column: number, expandedBox: MultiSelectionBox): boolean {
  return (
    row >= expandedBox.minI &&
    row <= expandedBox.maxI &&
    column >= expandedBox.minJ &&
    column <= expandedBox.maxJ
  );
}

/**
 * Computes whether there is a real (valid) selection within table bounds.
 */
export function computeHasRealSelection(selectionBox: MultiSelectionBox | null, table: Table): boolean {
  if (selectionBox == null) {
    return false;
  }
  const startIndex: TableIndex = [selectionBox.minI, selectionBox.minJ];
  const endIndex: TableIndex = [selectionBox.maxI, selectionBox.maxJ];
  return computeIsRealIndex(startIndex, table) && computeIsRealIndex(endIndex, table);
}

function clampIndexToTable(index: TableIndex, table: Table): TableIndex {
  const numRows = table.table.length;
  const numColumns = table.table[0].length;
  return [clamp(0, numRows - 1, index[0]), clamp(0, numColumns - 1, index[1])];
}

/**
 * Computes the expanded selection box that fully contains any merged regions intersecting
 * the selection. If either index is outside table bounds (ghost cells), returns the raw
 * normalized box without merge expansion.
 */
export function computeExpandedSelectionBox(
  multiStartIndex: TableIndex | null,
  multiEndIndex: TableIndex | null,
  table: Table,
  computedTable: Array<Array<EvaluatedCell>>
): MultiSelectionBox | null {
  const start = multiStartIndex;
  let end = multiEndIndex;

  if (start != null && end == null) {
    end = start;
  }
  if (start == null || end == null) {
    return null;
  }

  // If the user has a ghost cell selected, use that as the bounding box.
  if (!computeIsRealIndex(start, table) || !computeIsRealIndex(end, table)) {
    return TableUtil.indicesToCorners(start, end);
  }

  const multiBox = TableUtil.indicesToCorners(clampIndexToTable(start, table), clampIndexToTable(end, table));

  let {minI, maxI, minJ, maxJ} = multiBox;
  let prevMinI = -1;
  let prevMaxI = -1;
  let prevMinJ = -1;
  let prevMaxJ = -1;

  // Tries to expand the box coordinates until it can no longer do so.
  while (prevMinI !== minI || prevMaxI !== maxI || prevMinJ !== minJ || prevMaxJ !== maxJ) {
    prevMinI = minI;
    prevMaxI = maxI;
    prevMinJ = minJ;
    prevMaxJ = maxJ;
    for (let i = minI; i <= maxI; i++) {
      for (let j = minJ; j <= maxJ; j++) {
        const evaluatedCell = computedTable[i][j];
        if (evaluatedCell != null && evaluatedCell.mergedBox != null) {
          const {mergedBox} = evaluatedCell;
          minI = Math.min(minI, mergedBox.minI);
          maxI = Math.max(maxI, mergedBox.maxI);
          minJ = Math.min(minJ, mergedBox.minJ);
          maxJ = Math.max(maxJ, mergedBox.maxJ);
        }
      }
    }
  }
  return {minI, maxI, minJ, maxJ};
}

/**
 * Computes which full rows and/or full columns are selected, given an expanded selection box.
 * A full row is selected when the selection spans the full column range (respecting column boundaries).
 * A full column is selected when the selection spans all rows.
 *
 * The reported ranges are clamped to the current table bounds. The expanded box can hold indices
 * that fall outside the table (ghost cells, or a stale selection captured before rows/columns were
 * removed); clamping ensures the axis selection can never point past the table's last row/column.
 * Because a valid full-row/full-column selection is always within bounds, this clamp is a no-op for
 * real selections and only tames out-of-bounds indices that would otherwise be reported verbatim.
 */
export function computeMultiAxisDimSelection(
  expandedBox: MultiSelectionBox | null,
  tableMaxRowIndex: number,
  tableMinColumnIndex: number,
  tableMaxColumnIndex: number
): MultiAxisDimSelection {
  if (expandedBox == null) {
    return {};
  }
  const {minI, maxI, minJ, maxJ} = expandedBox;
  const clampRow = (index: number): number => clamp(0, tableMaxRowIndex, index);
  const clampCol = (index: number): number => clamp(0, tableMaxColumnIndex, index);
  return {
    row:
      minJ <= tableMinColumnIndex && maxJ >= tableMaxColumnIndex
        ? [clampRow(minI), clampRow(maxI)]
        : undefined,
    column: minI === 0 && maxI === tableMaxRowIndex ? [clampCol(minJ), clampCol(maxJ)] : undefined,
  };
}

/**
 * Returns the selection range for a given axis dimension and index, or null if the
 * index is not within the currently selected range for that dimension.
 */
export function computeSelectionRange(
  multiAxisDimSelection: MultiAxisDimSelection,
  dim: 'row' | 'column',
  index: number
): SelectionRange | null {
  const dimSelection = multiAxisDimSelection[dim];
  if (dimSelection == null) {
    return null;
  }
  const [minIndex, maxIndex] = dimSelection;
  if (minIndex <= index && index <= maxIndex) {
    return {minIndex, maxIndex};
  }
  return null;
}
